import type { QuizBlock } from '@/types/blocks'
import { useStrictLang } from './design/strictLangContext'
import type { ContentLang } from '@/features/editor/editorSlice'
import { QuizShell, QuizOptionCard, blockTheme } from '@teach/ui'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import { httpsMediaSrc } from '@/features/editor/lib/designUrl'
import { selectedOptions, toggleOption } from '../../preview/answerState'
import { answerableOptions } from '../../preview/quizOptions'
import type { PreviewRun } from '../../preview/previewRun'
import { getDisplayBody } from '../quizBodySync'
import { AnswerFeedback } from './AnswerFeedback'
import { optionState } from './optionAppearance'
import { PreviewShell } from './PreviewShell'
import { useFitOptions } from './useFitOptions'
import { useQuizMascot } from './quizMascotContext'
import { quizFeedback } from './quizFeedback'
import { pickMessages, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: QuizBlock
  contentLang: ContentLang
  /** Прохождение: варианты становятся кликабельными, кнопка — проверкой. */
  run?: PreviewRun
}

export const ChoiceQuizPreview = ({ block, contentLang, run }: Props) => {
  const m = useMessages(messages)
  // Экран ученика: кнопки и заголовки — на языке курса, как в плеере.
  const p = pickMessages(messages, contentLang)
  // Холст и миниатюры редактора — строго язык правки; прохождение — как плеер.
  const strictLang = useStrictLang() && (!run || run.onCanvas === true)
  const mascot = useQuizMascot(run?.verdict)
  const question = getDisplayBody(block, contentLang, strictLang).question ?? ''
  // Один список на превью и на проверку: `answerableOptions` — тот же счёт,
  // по которому решается, есть ли на экране чем отвечать.
  const options = answerableOptions(block, contentLang, strictLang)
  const rawImg = getLocalizedDisplay(block, 'image', contentLang, strictLang)
  // Авторский URL — гейтить перед DOM: только https, никогда не бросает.
  const img = rawImg ? httpsMediaSrc(rawImg) : null
  const chosen = run ? selectedOptions(run.answer) : []
  const locked = Boolean(run?.verdict)
  const optionsRef = useFitOptions(options.map((o) => o.text).join('\n'))

  return (
    <PreviewShell
      cta={run?.ctaLabel ?? p.confirm}
      ctaState={run?.ctaState}
      onCta={run?.onCta}
      ctaDisabled={run?.ctaDisabled}
      style={{ backgroundColor: blockTheme.quiz.bg }}
      contentClassName="min-h-0"
      verdictPlate={
        run ? (
          <AnswerFeedback
            lang={contentLang}
            verdict={run.verdict}
            text={quizFeedback(block, contentLang, run.verdict)}
          />
        ) : undefined
      }
    >
      <QuizShell
        question={question}
        mascot={mascot}
        maxQuestionLines={2}
        // Пустые поля автору — серыми подсказками, как в панели; ученик их не увидит.
        placeholder={run && !run.onCanvas ? undefined : p.questionPlaceholder}
      >
        {img ? (
          // Как `.image` плеера (QuizBlock.module.css): до 172px, скругление 8px, cover.
          <img
            src={img}
            alt=""
            className="mb-6 h-auto max-h-[172px] w-full rounded-lg object-cover"
          />
        ) : null}
        <div ref={optionsRef} className="flex w-full flex-col gap-2">
          {options.map(({ text, index, isCorrect }) => (
            <QuizOptionCard
              key={index}
              text={text}
              placeholder={run && !run.onCanvas ? undefined : p.variantN(index + 1)}
              state={run ? optionState(chosen.includes(index), isCorrect, run.verdict) : 'idle'}
              multi={block.multiAnswer}
              disabled={locked}
              onClick={
                run
                  ? () => run.setAnswer(toggleOption(run.answer, index, block.multiAnswer))
                  : undefined
              }
            />
          ))}
        </div>
      </QuizShell>
    </PreviewShell>
  )
}
